// ============================================================
// 广发圩 - 工具函数
// ============================================================

// ─── 格式化 ─────────────────────────────────────────────────

/** 格式化积分数值（千分位） */
export function formatPoints(points: number): string {
  return points.toLocaleString('zh-CN')
}

/** 格式化金额（分转元） */
export function formatMoney(fen: number): string {
  return `¥${(fen / 100).toFixed(2)}`
}

/** 格式化时间：相对时间 */
export function formatRelativeTime(dateStr: string): string {
  const now = Date.now()
  const date = new Date(dateStr).getTime()
  const diff = now - date

  const minute = 60 * 1000
  const hour = 60 * minute
  const day = 24 * hour

  if (diff < minute) return '刚刚'
  if (diff < hour) return `${Math.floor(diff / minute)}分钟前`
  if (diff < day) return `${Math.floor(diff / hour)}小时前`
  if (diff < 7 * day) return `${Math.floor(diff / day)}天前`

  return formatDate(dateStr)
}

/** 格式化日期：YYYY-MM-DD */
export function formatDate(dateStr: string): string {
  const d = new Date(dateStr)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** 格式化日期时间：YYYY-MM-DD HH:mm */
export function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr)
  const date = formatDate(dateStr)
  const h = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${date} ${h}:${min}`
}

/** 计算截止时间倒计时 */
export function formatDeadline(deadline: string): string {
  const now = Date.now()
  const end = new Date(deadline).getTime()
  const diff = end - now

  if (diff <= 0) return '已截止'

  const day = 24 * 60 * 60 * 1000
  const hour = 60 * 60 * 1000
  const minute = 60 * 1000

  const days = Math.floor(diff / day)
  const hours = Math.floor((diff % day) / hour)

  if (days > 0) return `还剩 ${days} 天 ${hours} 小时`
  if (hours > 0) return `还剩 ${hours} 小时`

  const minutes = Math.floor((diff % hour) / minute)
  return `还剩 ${minutes} 分钟`
}

// ─── 校验 ───────────────────────────────────────────────────

/** 校验手机号 */
export function isValidPhone(phone: string): boolean {
  return /^1[3-9]\d{9}$/.test(phone)
}

/** 校验身份证号 */
export function isValidIdCard(idCard: string): boolean {
  return /^\d{17}[\dXx]$/.test(idCard)
}

/** 校验验证码（6位数字） */
export function isValidSmsCode(code: string): boolean {
  return /^\d{6}$/.test(code)
}

// ─── 手机号脱敏 ─────────────────────────────────────────────

export function maskPhone(phone: string): string {
  if (phone.length !== 11) return phone
  return `${phone.slice(0, 3)}****${phone.slice(7)}`
}

// ─── 存储 ───────────────────────────────────────────────────

const STORAGE_PREFIX = 'tb_'

export const storage = {
  get<T = string>(key: string): T | null {
    try {
      const val = localStorage.getItem(STORAGE_PREFIX + key)
      if (val === null) return null
      return JSON.parse(val) as T
    } catch {
      return null
    }
  },
  set(key: string, value: unknown): void {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value))
  },
  remove(key: string): void {
    localStorage.removeItem(STORAGE_PREFIX + key)
  },
  clear(): void {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(STORAGE_PREFIX))
      .forEach((k) => localStorage.removeItem(k))
  },
}
